// Store.js
import { Link } from 'react-router-dom';
import React, { useEffect, useMemo, useState } from 'react'; 
import { FaChevronLeft, FaChevronRight, FaSlidersH, FaTimes } from 'react-icons/fa';
import './Store.css';
import { yoyoData } from './yoyoData';
import { resolveImageUrl } from '../utils/resolveImageUrl';
import getApiBase from '../utils/apiBase';
import Seo from '../components/Seo';
import { useI18n } from '../i18n/I18nContext';
import { getProductPath } from '../utils/productSlug';
import { trackAnalyticsEvent } from '../utils/analytics';
import {
  PRODUCT_FILTERS,
  buildFilterSections,
  productMatchesFilters,
  withProductTags,
} from './productFilters';

const API_BASE = getApiBase();
const PRODUCTS_PER_PAGE = 9;
const TAG_TRANSLATION_KEYS = {
  'Two-Hand Looping': 'store.tag.twoHandLooping',
  'Yo-Yo Parts': 'store.tag.parts',
  Offstring: 'store.tag.offstring',
  Freehand: 'store.tag.freehand',
  'Single-Hand String': 'store.tag.singleHandString',
  'Two-Hand String': 'store.tag.twoHandString',
};

const createEmptyFilters = () => (
  PRODUCT_FILTERS.reduce((filters, filter) => ({
    ...filters,
    [filter.key]: [],
  }), {})
);

const Store = () => {
  const [products, setProducts] = useState([]);
  const [filterSections, setFilterSections] = useState(() => buildFilterSections([]));
  const [selectedFilters, setSelectedFilters] = useState(createEmptyFilters);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const { t, formatNumber } = useI18n();
  const displayTag = (value) => TAG_TRANSLATION_KEYS[value] ? t(TAG_TRANSLATION_KEYS[value]) : value;

  useEffect(() => {
    let isMounted = true;

    const loadFallbackProducts = () => {
      const fallbackCatalog = yoyoData.map(withProductTags).filter((yoyo) => yoyo.id !== 7);
      const filteredProducts = fallbackCatalog.filter((product) => (
        productMatchesFilters(product, selectedFilters)
      ));
      const fallbackTotalPages = Math.max(1, Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE));
      const fallbackPage = Math.min(currentPage, fallbackTotalPages);
      const start = (fallbackPage - 1) * PRODUCTS_PER_PAGE;

      setProducts(filteredProducts.slice(start, start + PRODUCTS_PER_PAGE));
      setFilterSections(buildFilterSections(fallbackCatalog));
      setTotalItems(filteredProducts.length);
      setTotalPages(fallbackTotalPages);
      setCurrentPage(fallbackPage);
    };

    const loadProducts = async () => {
      setLoadingProducts(true);
      try {
        const params = new URLSearchParams({
          page: String(currentPage),
          per_page: String(PRODUCTS_PER_PAGE),
          include_custom: "0",
        });

        PRODUCT_FILTERS.forEach((filter) => {
          (selectedFilters[filter.key] || []).forEach((value) => {
            params.append(filter.key, value);
          });
        });

        const response = await fetch(`${API_BASE}/api/products?${params.toString()}`);
        if (!response.ok) {
          throw new Error("Failed to fetch");
        }
        const data = await response.json();
        if (!isMounted) return;
        if (Array.isArray(data?.items)) {
          const nextPage = Number(data.page) || 1;
          setProducts(data.items.map(withProductTags));
          setFilterSections(Array.isArray(data.filters) ? data.filters : buildFilterSections(data.items));
          setTotalItems(Number(data.total) || 0);
          setTotalPages(Math.max(1, Number(data.totalPages) || 1));
          if (nextPage !== currentPage) {
            setCurrentPage(nextPage);
          }
          return;
        }

        throw new Error("Unexpected response");
      } catch (error) {
        if (isMounted) {
          loadFallbackProducts();
        }
      } finally {
        if (isMounted) {
          setLoadingProducts(false);
        }
      }
    };

    loadProducts();
    return () => {
      isMounted = false;
    };
  }, [currentPage, selectedFilters]);

  const safeCurrentPage = Math.min(currentPage, totalPages);
  const pageStartIndex = (safeCurrentPage - 1) * PRODUCTS_PER_PAGE;
  const activeFilterChips = useMemo(
    () => PRODUCT_FILTERS.flatMap((filter) => (
      (selectedFilters[filter.key] || []).map((value) => ({
        key: `${filter.key}-${value}`,
        filterKey: filter.key,
        label: filter.label,
        value,
      }))
    )),
    [selectedFilters]
  );
  const pageNumbers = useMemo(() => {
    const pages = [];
    const addPage = (page) => {
      if (!pages.includes(page)) {
        pages.push(page);
      }
    };
    const start = Math.max(1, safeCurrentPage - 1);
    const end = Math.min(totalPages, safeCurrentPage + 1);

    addPage(1);
    if (start > 2) {
      pages.push("start-gap");
    }
    for (let page = start; page <= end; page += 1) {
      addPage(page);
    }
    if (end < totalPages - 1) {
      pages.push("end-gap");
    }
    addPage(totalPages);

    return pages;
  }, [safeCurrentPage, totalPages]);

  const toggleFilter = (filterKey, value) => {
    setSelectedFilters((current) => {
      const values = current[filterKey] || [];
      const nextValues = values.includes(value)
        ? values.filter((item) => item !== value)
        : [...values, value];

      return {
        ...current,
        [filterKey]: nextValues,
      };
    });
    setCurrentPage(1);
  };

  const removeFilter = (filterKey, value) => {
    setSelectedFilters((current) => ({
      ...current,
      [filterKey]: (current[filterKey] || []).filter((item) => item !== value),
    }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSelectedFilters(createEmptyFilters());
    setCurrentPage(1);
  };

  const goToPage = (page) => {
    setCurrentPage(Math.min(Math.max(page, 1), totalPages));
  };

  const siteUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'YCH-YOYO Store',
    url: `${siteUrl}/store`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: products.map((product, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `${siteUrl}${product.path || getProductPath(product, products)}`,
        name: product.name,
      })),
    },
  };
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: t('nav.home'),
        item: `${siteUrl}/`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: t('nav.store'),
        item: `${siteUrl}/store`,
      },
    ],
  };

  return (
    <div className="store-page">
      <Seo
        title={t('store.seoTitle')}
        description={t('store.seoDescription')}
        path="/store"
        image="/images/blitzball_main.jpg"
        structuredData={[structuredData, breadcrumbSchema]}
      />
      <section className="store-hero">
        <div>
          <span className="store-eyebrow">{t('store.eyebrow')}</span>
          <h1>{t('store.title')}</h1>
          <p>{t('store.intro')}</p>
        </div>
      </section>

      <div className="store-layout">
        <aside className="store-filters" aria-label={t('store.filters')}>
          <div className="filter-header">
            <div>
              <span><FaSlidersH aria-hidden="true" /> {t('store.filtersLabel')}</span>
              <h2>{t('store.findSetup')}</h2>
            </div>
            {activeFilterChips.length > 0 && (
              <button data-analytics-target="store.filters_clear" type="button" className="clear-filters" onClick={clearFilters}>
                <FaTimes aria-hidden="true" />
                {t('store.clear')}
              </button>
            )}
          </div>

          {filterSections.map((section) => (
            <fieldset className="filter-section" key={section.key}>
              <legend>{t(`store.filter.${section.key}`)}</legend>
              <div className="filter-options">
                {section.options.map((option) => {
                  const checked = (selectedFilters[section.key] || []).includes(option.value);
                  const disabled = option.count === 0 && !checked;

                  return (
                    <label
                      className={`filter-option${checked ? ' selected' : ''}${disabled ? ' disabled' : ''}`}
                      key={option.value}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={disabled}
                        onChange={() => {
                          toggleFilter(section.key, option.value);
                          if (PRODUCT_FILTERS.some((filter) => filter.key === section.key)) {
                            trackAnalyticsEvent('click', { target: `store.filter.${section.key}` });
                          }
                        }}
                      />
                      <span className="filter-option-text">{displayTag(option.value)}</span>
                      <span className="filter-option-count">{option.count}</span>
                    </label>
                  );
                })}
              </div>
            </fieldset>
          ))}
        </aside>

        <section className="store-results" aria-label={t('store.products')}>
          <div className="store-results-bar">
            <div>
              <span className="store-eyebrow">{t('store.products')}</span>
              <h2>{t('store.itemCount', { count: totalItems })}</h2>
            </div>
            <p>
              {t('store.showing', {
                start: totalItems ? pageStartIndex + 1 : 0,
                end: Math.min(pageStartIndex + PRODUCTS_PER_PAGE, totalItems),
                total: totalItems,
              })}
            </p>
          </div>

          {activeFilterChips.length > 0 && (
            <div className="active-filter-chips" aria-label={t('store.activeFilters')}>
              {activeFilterChips.map((chip) => (
                <button
                  type="button"
                  key={chip.key}
                  className="active-filter-chip"
                  onClick={() => removeFilter(chip.filterKey, chip.value)}
                >
                  <span>{t(`store.filter.${chip.filterKey}`)}: {displayTag(chip.value)}</span>
                  <FaTimes aria-hidden="true" />
                </button>
              ))}
            </div>
          )}

          {loadingProducts && !products.length ? (
            <div className="store-empty">
              <p>{t('store.loading')}</p>
            </div>
          ) : products.length > 0 ? (
            <div className="store-grid">
              {products.map(yoyo => {
                const cardTags = [
                  ...(yoyo.tags?.type || []),
                  ...(yoyo.tags?.group || []),
                ].slice(0, 3);

                return (
                  <div className="yoyo-card" key={yoyo.id}>
                    <Link to={yoyo.path || getProductPath(yoyo, products)} className="yoyo-link">
                      <img src={resolveImageUrl(yoyo.imageUrl)} alt={t('store.productAlt', { name: yoyo.name })} className="yoyo-image" />
                    </Link>
                    <div className="yoyo-info">
                      {cardTags.length > 0 && (
                        <div className="yoyo-tags" aria-label={t('store.productTags', { name: yoyo.name })}>
                          {cardTags.map((tag) => (
                            <span key={tag}>{displayTag(tag)}</span>
                          ))}
                        </div>
                      )}
                      <h3 className="yoyo-name">{yoyo.name}</h3>
                      <p className="yoyo-price">{formatNumber(Number(yoyo.price), { style: 'currency', currency: 'USD' })}</p>
                      <p className="yoyo-copy">
                        {t('store.productCopy', { brand: (yoyo.tags?.brand || []).join(', ') || 'YCH-YOYO' })}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="store-empty">
              <h2>{t('store.emptyTitle')}</h2>
              <p>{t('store.emptyMessage')}</p>
              <button type="button" onClick={clearFilters}>
                <FaTimes aria-hidden="true" />
                {t('store.clearFilters')}
              </button>
            </div>
          )}

          {totalPages > 1 && (
            <nav className="store-pagination" aria-label={t('store.pagination')}>
              <button
                data-analytics-target="store.pagination.previous"
                type="button"
                className="pagination-control"
                onClick={() => goToPage(safeCurrentPage - 1)}
                disabled={safeCurrentPage === 1}
                aria-label={t('store.previousPage')}
              >
                <FaChevronLeft aria-hidden="true" />
              </button>
              {pageNumbers.map((page) => (
                typeof page === "number" ? (
                  <button
                    data-analytics-target="store.pagination.page"
                    type="button"
                    key={page}
                    className={`pagination-page${page === safeCurrentPage ? ' active' : ''}`}
                    onClick={() => goToPage(page)}
                    aria-current={page === safeCurrentPage ? "page" : undefined}
                  >
                    {page}
                  </button>
                ) : (
                  <span className="pagination-gap" key={page}>...</span>
                )
              ))}
              <button
                data-analytics-target="store.pagination.next"
                type="button"
                className="pagination-control"
                onClick={() => goToPage(safeCurrentPage + 1)}
                disabled={safeCurrentPage === totalPages}
                aria-label={t('store.nextPage')}
              >
                <FaChevronRight aria-hidden="true" />
              </button>
            </nav>
          )}
        </section>
      </div>
    </div>
    
  );
};

export default Store;
